/** Time-entries formatting helpers, aggregation algorithms, and CSV columns.
 *
 * Source of truth for v2 fields: `docs/models/time-tracking-v2.md`. The summary
 * endpoint returns precise totals; group-by breakdowns are computed client-side
 * from a single page of `useTimeEntries({ pageSize: 500 })`. */

import type { TimeEntry, TimeEntrySummary } from "../../types";
import { exportData } from "../../utils/export";

// ---------------------------------------------------------------------------
// Enums & display labels
// ---------------------------------------------------------------------------

export type BillingStatus = TimeEntry["billingStatus"];
export type RunStatus = TimeEntry["status"];

export const BILLING_STATUS_LABELS: Record<BillingStatus, string> = {
  billable: "计费",
  non_billable: "不计费",
  billed: "已开票",
};

export const RUN_STATUS_LABELS: Record<RunStatus, string> = {
  running: "进行中",
  stopped: "已停止",
};

// ---------------------------------------------------------------------------
// Duration & currency formatting
// ---------------------------------------------------------------------------

/** Format seconds as `H小时M分` (omit zero components; seconds only when <60). */
export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "0秒";
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.floor(totalSeconds % 60);
  if (hours > 0) return `${hours}小时${mins > 0 ? `${mins}分` : ""}`;
  if (mins > 0) return `${mins}分${secs > 0 ? `${secs}秒` : ""}`;
  return `${secs}秒`;
}

/** Decimal-hours representation, one fractional digit (e.g. 1.5h). */
export function formatDecimalHours(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "0.0";
  return (totalSeconds / 3600).toFixed(1);
}

/** Currency formatter for CNY. Null/undefined → em dash. */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  return `¥${amount.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Short datetime label: `MM-DD HH:mm`. */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${mm}-${dd} ${hh}:${mi}`;
}

// ---------------------------------------------------------------------------
// Date range presets & ISO helpers
// ---------------------------------------------------------------------------

export type DateRangePreset = "7d" | "30d" | "90d" | "all";

export const DATE_RANGE_PRESET_LABELS: Record<DateRangePreset, string> = {
  "7d": "最近 7 天",
  "30d": "最近 30 天",
  "90d": "最近 90 天",
  all: "全部",
};

/** Returns ISO string for `days` ago at start-of-day; null for "all". */
export function dateRangeToFrom(preset: DateRangePreset): string | undefined {
  if (preset === "all") return undefined;
  const days = Number(preset.replace("d", ""));
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

/** Build an ISO `YYYY-MM-DDTHH:mm:ss` from a Date object. */
export function toLocalIsoString(d: Date): string {
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60_000);
  return local.toISOString().slice(0, 19);
}

// ---------------------------------------------------------------------------
// Business rules
// ---------------------------------------------------------------------------

/** `billed` records are immutable per v2 §3.1. */
export function isImmutable(entry: TimeEntry): boolean {
  return entry.billingStatus === "billed";
}

// ---------------------------------------------------------------------------
// Aggregation (client-side, single-page strategy)
// ---------------------------------------------------------------------------

export interface MatterBreakdownRow {
  matterId: string | null;
  matterName: string;
  seconds: number;
  amount: number;
  entryCount: number;
}

export interface ActionTypeBreakdownRow {
  actionTypeId: string | null;
  actionTypeName: string;
  seconds: number;
  entryCount: number;
}

export interface DailyTrendPoint {
  date: string; // YYYY-MM-DD
  label: string; // MM-DD
  seconds: number;
}

export interface EmployeeBreakdownRow {
  employeeId: string;
  employeeName: string;
  seconds: number;
  amount: number;
  entryCount: number;
}

/** Bucket entries by matter. Uncategorized entries roll into "未分类工时". */
export function groupByMatter(entries: TimeEntry[]): MatterBreakdownRow[] {
  const map = new Map<string, MatterBreakdownRow>();
  for (const e of entries) {
    const key = e.matterId ?? "uncategorized";
    const existing = map.get(key);
    if (existing) {
      existing.seconds += e.durationSeconds;
      existing.amount += Number(e.computedAmount ?? 0);
      existing.entryCount += 1;
    } else {
      map.set(key, {
        matterId: e.matterId,
        matterName: e.matterName?.trim() || "未分类工时",
        seconds: e.durationSeconds,
        amount: Number(e.computedAmount ?? 0),
        entryCount: 1,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.seconds - a.seconds);
}

/** Bucket entries by action type. Unclassified → "未分类". */
export function groupByActionType(entries: TimeEntry[]): ActionTypeBreakdownRow[] {
  const map = new Map<string, ActionTypeBreakdownRow>();
  for (const e of entries) {
    const key = e.actionTypeId ?? "unclassified";
    const existing = map.get(key);
    if (existing) {
      existing.seconds += e.durationSeconds;
      existing.entryCount += 1;
    } else {
      map.set(key, {
        actionTypeId: e.actionTypeId,
        actionTypeName: e.actionTypeName?.trim() || "未分类",
        seconds: e.durationSeconds,
        entryCount: 1,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.seconds - a.seconds);
}

/** Bucket entries by employee. */
export function groupByEmployee(entries: TimeEntry[]): EmployeeBreakdownRow[] {
  const map = new Map<string, EmployeeBreakdownRow>();
  for (const e of entries) {
    const key = e.employeeId;
    const existing = map.get(key);
    if (existing) {
      existing.seconds += e.durationSeconds;
      existing.amount += Number(e.computedAmount ?? 0);
      existing.entryCount += 1;
    } else {
      map.set(key, {
        employeeId: e.employeeId,
        employeeName: e.employeeName?.trim() || "未知",
        seconds: e.durationSeconds,
        amount: Number(e.computedAmount ?? 0),
        entryCount: 1,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.seconds - a.seconds);
}

/** Daily trend: fill missing days in the range with zero buckets.
 *
 * `days` is the visible window; the first day = today - (days-1). Entries are
 * bucketed by their `startTime`'s local date. */
export function buildDailyTrend(entries: TimeEntry[], days: number): DailyTrendPoint[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const buckets: DailyTrendPoint[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    buckets.push({
      date: `${yyyy}-${mm}-${dd}`,
      label: `${mm}-${dd}`,
      seconds: 0,
    });
  }

  const index = new Map(buckets.map((b, i) => [b.date, i]));
  for (const e of entries) {
    const d = new Date(e.startTime);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const key = `${yyyy}-${mm}-${dd}`;
    const i = index.get(key);
    if (i !== undefined) buckets[i].seconds += e.durationSeconds;
  }
  return buckets;
}

// ---------------------------------------------------------------------------
// Summary fallback (when backend summary endpoint is unavailable)
// ---------------------------------------------------------------------------

/** Compute summary from a list when the summary endpoint has not been called.
 *  Useful for report tabs that already paginate the full list. */
export function computeSummaryFromEntries(entries: TimeEntry[]): TimeEntrySummary {
  let total = 0;
  let billable = 0;
  let nonBillable = 0;
  let billed = 0;
  let amount = 0;
  for (const e of entries) {
    total += e.durationSeconds;
    amount += Number(e.computedAmount ?? 0);
    if (e.billingStatus === "billable") billable += e.durationSeconds;
    else if (e.billingStatus === "non_billable") nonBillable += e.durationSeconds;
    else if (e.billingStatus === "billed") billed += e.durationSeconds;
  }
  return {
    totalSeconds: total,
    totalAmount: amount,
    billableSeconds: billable,
    nonBillableSeconds: nonBillable,
    billedSeconds: billed,
    entryCount: entries.length,
  };
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

const CSV_HEADERS = [
  "开始时间",
  "结束时间",
  "时长(小时)",
  "描述",
  "关联事项",
  "动作分类",
  "被记录员工",
  "计费状态",
  "费率(元/小时)",
  "金额(元)",
  "私有",
];

/** Export a list of time entries to CSV. The `isPrivate` column is included
 *  deliberately so exporters notice when private records are present. */
export function exportTimeEntriesCsv(
  entries: TimeEntry[],
  filenamePrefix = "工时记录",
): void {
  const rows = entries.map((e) => [
    formatDateTime(e.startTime),
    e.endTime ? formatDateTime(e.endTime) : "",
    (e.durationSeconds / 3600).toFixed(2),
    e.description ?? "",
    e.matterName ?? "(未分类)",
    e.actionTypeName ?? "(未分类)",
    e.employeeName ?? "",
    BILLING_STATUS_LABELS[e.billingStatus],
    e.hourlyRate != null ? e.hourlyRate.toFixed(2) : "",
    e.computedAmount != null ? e.computedAmount.toFixed(2) : "",
    e.isPrivate ? "是" : "否",
  ]);
  exportData(filenamePrefix, "工时记录", CSV_HEADERS, rows);
}
